import { supabase } from "@/integrations/supabase/client";
import type { ParsedImportBook } from "./libraryImport";
import { parseTags } from "@/utils/parseTags";

export interface Transmission {
  id: number;
  title: string;
  author: string;
  status: "reading" | "read" | "want-to-read";
  tags: string[];
  notes: string;
  cover_url: string;
  rating: {
    truth?: boolean;
    confirmed?: boolean;
    disrupted?: boolean;
    rewired?: boolean;
  };
  user_id: string;
  created_at: string;
  publisher_series_id?: string;
  publisher_series?: {
    id: string;
    name: string;
    publisher: string;
    description: string;
    logo_url?: string;
    badge_emoji: string;
  } | null;
  isbn?: string;
  apple_link?: string;
  open_count?: number;
  publication_year?: number;
  narrative_time_period?: string;
  historical_context_tags?: string[];
  temporal_context_tags?: string[];
  is_favorite?: boolean;
  protagonist?: string;
  protagonist_portrait_url?: string;
  protagonist_intro?: string;
  star_rating?: number | null;
}

export const saveTransmission = async (transmission: Omit<Transmission, 'id' | 'user_id' | 'created_at'>) => {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  console.log('Saving transmission for user:', user.email);

  // Check for existing transmission with same title and author to prevent duplicates
  const { data: existing } = await supabase
    .from('transmissions')
    .select('id, created_at')
    .eq('user_id', user.id)
    .eq('title', transmission.title)
    .eq('author', transmission.author)
    .maybeSingle();

  if (existing) {
    console.log('Transmission already exists, skipping duplicate:', transmission.title);
    const addedDate = new Date(existing.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const error = new Error(`DUPLICATE:${transmission.title}:${addedDate}`);
    throw error;
  }

  const { data, error } = await supabase
    .from('transmissions')
    .insert([{
      title: transmission.title,
      author: transmission.author,
      cover_url: transmission.cover_url,
      tags: JSON.stringify(transmission.tags),
      resonance_labels: JSON.stringify(transmission.rating),
      notes: transmission.notes,
      publisher_series_id: transmission.publisher_series_id,
      isbn: transmission.isbn,
      apple_link: transmission.apple_link,
      open_count: transmission.open_count || 0,
      status: transmission.status || 'read',
      rating: transmission.star_rating ?? null,
      user_id: user.id
    }] as any)
    .select()
    .single();

  if (error) {
    console.error('Error saving transmission:', error);
    throw error;
  }
  
  console.log('Transmission saved successfully:', data);
  
  /**
   * Infer the protagonist name — but never write one another row already uses.
   *
   * THIS IS THE PATH THAT BROKE THE iOS APP, so the guard is the point and the
   * inference is incidental.
   *
   * `transmissions` is shared with the Leafnode iOS app, which de-duplicates
   * its catalogue by protagonist NAME. On 23 August a hand-added second
   * "Flatland" came through here, the model answered "Arthur", and that was
   * written straight in. A later rename to "A. Square" collided with id 8 — the
   * real A. Square, with the portrait, the intro, the bespoke voice and the
   * free-tier flag — and the app dropped the original from its catalogue
   * entirely. He leads its opening Spotlight row. Nothing errored on either
   * side.
   *
   * The guard matters more now than it did then. infer-protagonist was changed
   * on 24 August to consult the catalogue before asking a model, which is
   * right for accuracy and wrong for this: it now returns the INCUMBENT's exact
   * name, so adding a second copy of any catalogued book would reliably
   * manufacture the collision instead of occasionally stumbling into it.
   *
   * The comparison deliberately mirrors the CONSUMER's rule — lower(trim(...)),
   * which is how the app collapses names — not a cleverer fold of our own. A
   * stricter rule here would refuse names the app is perfectly happy with.
   *
   * FAILS CLOSED. If the collision check itself errors we write nothing: a row
   * with no protagonist costs a chat affordance on one book, and a duplicate
   * name costs a character on the App Store product's first screen.
   */
  void (async () => {
    try {
      const { data: inferData } = await supabase.functions.invoke('infer-protagonist', {
        body: { bookTitle: transmission.title, bookAuthor: transmission.author },
      });

      const inferred = String(inferData?.protagonist ?? '').trim();
      if (!inferred) return;

      const { data: existing, error: clashErr } = await supabase
        .from('transmissions')
        .select('id, protagonist')
        .not('protagonist', 'is', null)
        .neq('id', data.id);

      if (clashErr) {
        console.warn(
          'Protagonist guard could not read the catalogue — leaving protagonist unset rather than risk a duplicate.',
          clashErr.message,
        );
        return;
      }

      const key = inferred.toLowerCase();
      const clash = (existing ?? []).find(
        (r: { id: number; protagonist: string | null }) =>
          String(r.protagonist ?? '').trim().toLowerCase() === key,
      );

      if (clash) {
        console.warn(
          `Protagonist "${inferred}" already belongs to transmission ${clash.id} — not writing a second one. ` +
            'The iOS app de-duplicates by name and a duplicate can displace the original.',
        );
        return;
      }

      const { error: updateErr } = await supabase
        .from('transmissions')
        .update({ protagonist: inferred })
        .eq('id', data.id);

      if (updateErr) console.error('Error updating protagonist:', updateErr);
      else console.log('Protagonist set to:', inferred);
    } catch (err) {
      console.error('Protagonist inference failed:', err);
    }
  })();

  // Notify followers about the new transmission
  try {
    const { data: followers } = await supabase
      .from('user_follows')
      .select('follower_id')
      .eq('following_id', user.id);
    
    if (followers && followers.length > 0) {
      const notifications = followers.map(f => ({
        user_id: f.follower_id,
        type: 'new_transmission',
        from_user_id: user.id,
        transmission_id: data.id
      }));
      
      await supabase.from('notifications').insert(notifications);
    }
  } catch (notifError) {
    console.error('Error creating notifications:', notifError);
    // Don't fail the transmission save for notification errors
  }
  
  return data;
};

// The transmissions UPDATE policy is `auth.uid()::text = user_id`, so a logged-out
// visitor never matches a row: the update touches 0 rows and the trailing .single()
// throws PGRST116. Callers that persist opportunistically (e.g. backfilling an Apple
// Books link from a catalog card) should check this first rather than fire a request
// that cannot succeed. Mirrors canWriteImageCache() in imageCacheService.
export const canPersistTransmission = async (): Promise<boolean> => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session?.user?.id;
};

export const updateTransmission = async (id: number, transmission: Partial<Omit<Transmission, 'id' | 'user_id' | 'created_at'>>) => {
  const updateData: any = {};
  
  if (transmission.title !== undefined) updateData.title = transmission.title;
  if (transmission.author !== undefined) updateData.author = transmission.author;
  if (transmission.cover_url !== undefined) updateData.cover_url = transmission.cover_url;
  if (transmission.tags !== undefined) updateData.tags = JSON.stringify(transmission.tags);
  if (transmission.rating !== undefined) updateData.resonance_labels = JSON.stringify(transmission.rating);
  if (transmission.notes !== undefined) updateData.notes = transmission.notes;
  if (transmission.publisher_series_id !== undefined) updateData.publisher_series_id = transmission.publisher_series_id;
  if (transmission.isbn !== undefined) updateData.isbn = transmission.isbn;
  if (transmission.apple_link !== undefined) updateData.apple_link = transmission.apple_link;
  if (transmission.is_favorite !== undefined) updateData.is_favorite = transmission.is_favorite;
  if (transmission.status !== undefined) updateData.status = transmission.status;
  if (transmission.star_rating !== undefined) updateData.rating = transmission.star_rating;
  
  // Special handling for open_count increment
  if (transmission.open_count !== undefined) {
    const { data: currentData } = await supabase
      .from('transmissions')
      .select('open_count')
      .eq('id', id)
      .single();
    
    updateData.open_count = (currentData?.open_count || 0) + 1;
  }

  console.log('Updating transmission:', id, updateData);

  const { data, error } = await supabase
    .from('transmissions')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating transmission:', error);
    throw error;
  }
  
  console.log('Transmission updated successfully:', data);
  return data;
};

// Improved version with better error handling and fallbacks.
//
// The default used to be 50, which silently truncated: a library of 78 books
// rendered 50 everywhere except the Neural Map, the one caller that passed a
// number of its own. Nothing said the list was cut short, so the counts simply
// disagreed with each other. 500 clears the largest library several times over
// and stays well inside PostgREST's 1000-row ceiling; callers that genuinely
// want a handful still pass their own limit.
export const getTransmissions = async (limit = 500): Promise<Transmission[]> => {
  try {
    // Use getSession() instead of getUser() — getSession reads from local storage
    // and doesn't make a network call, avoiding silent failures on mobile Safari
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Error getting session:', sessionError);
      throw new Error('Authentication error');
    }
    
    const user = session?.user;
    if (!user) {
      console.log('No authenticated session found');
      return []; // Return empty array instead of throwing error
    }

    console.log('Fetching transmissions for user:', user.email);

    const { data, error } = await supabase
      .from('transmissions')
      .select(`
        *
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching transmissions:', error);
      // Throw rather than returning []: an empty array is indistinguishable
      // from "this user has no books", so a failed load used to render the
      // "add your first book" empty state. Callers all catch and show an
      // error state instead.
      throw error;
    }

    console.log('Fetched transmissions:', data?.length || 0);
    
    return (data || []).map(item => ({
      id: item.id,
      title: item.title || '',
      author: item.author || '',
      status: ((item as any).status || 'read') as Transmission['status'],
      star_rating: (item as any).rating ?? null,
      tags: item.tags ? parseTags(item.tags) : [],
      notes: item.notes || '',
      cover_url: item.cover_url || '',
      rating: item.resonance_labels ? JSON.parse(item.resonance_labels) : {},
      user_id: item.user_id || '',
      created_at: item.created_at,
      publisher_series_id: item.publisher_series_id,
      publisher_series: null, // Set to null since we're not joining
      isbn: item.isbn,
      apple_link: item.apple_link,
      open_count: item.open_count || 0,
      publication_year: item.publication_year,
      narrative_time_period: item.narrative_time_period,
      historical_context_tags: item.historical_context_tags,
      is_favorite: item.is_favorite || false,
      protagonist: item.protagonist || undefined,
      protagonist_portrait_url: item.protagonist_portrait_url || undefined,
      protagonist_intro: item.protagonist_intro || undefined
    }));
  } catch (error) {
    console.error('Unexpected error in getTransmissions:', error);
    // Rethrow so the caller can distinguish failure from an empty library.
    // The genuine "no session" case still returns [] above.
    throw error;
  }
};

export const deleteTransmission = async (id: number) => {
  console.log('Deleting transmission:', id);
  
  const { error } = await supabase
    .from('transmissions')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting transmission:', error);
    throw error;
  }

  console.log('Transmission deleted successfully');
};

export interface ImportResult {
  imported: number;
  skippedDuplicates: number;
  skippedInvalid: number;
  failed: number;
}

// A large import must never behave differently from adding books by hand — the
// iOS app reads this same table, so every imported row is built to be
// indistinguishable from one `saveTransmission` would write: same columns, and
// `user_id` always the signed-in user (RLS would reject anything else anyway).
// Deliberately leaner than saveTransmission in two ways: it does NOT fire the
// per-book protagonist-inference edge function or follower notifications —
// running those hundreds of times over would hammer paid functions for no
// benefit. Protagonists still get filled later by the existing enrichment cron.
const IMPORT_MAX = 2000;   // a generous ceiling; guards against a runaway file
const IMPORT_BATCH = 40;

export const importTransmissions = async (
  books: ParsedImportBook[],
  onProgress?: (done: number, total: number) => void
): Promise<ImportResult> => {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Dedupe against the whole existing library in one fetch, then within the
  // file itself, so re-running an import (or a file with repeats) is a no-op.
  const existing = await getTransmissions();
  const seen = new Set(
    existing.map(b => `${b.title.trim().toLowerCase()}|${b.author.trim().toLowerCase()}`)
  );

  let skippedDuplicates = 0;
  let skippedInvalid = 0;
  const toInsert: ParsedImportBook[] = [];
  for (const b of books) {
    const title = (b.title || '').trim();
    const author = (b.author || '').trim();
    if (!title || !author) { skippedInvalid++; continue; }
    const key = `${title.toLowerCase()}|${author.toLowerCase()}`;
    if (seen.has(key)) { skippedDuplicates++; continue; }
    seen.add(key);
    toInsert.push({ ...b, title, author });
    if (toInsert.length >= IMPORT_MAX) break;
  }

  // Exactly the shape saveTransmission writes (minus its fire-and-forget extras).
  const buildRow = (b: ParsedImportBook) => ({
    title: b.title,
    author: b.author,
    isbn: b.isbn ?? null,
    cover_url: '',
    tags: JSON.stringify([]),
    resonance_labels: JSON.stringify({}),
    notes: '',
    status: b.status,
    rating: b.rating ?? null,
    publication_year: b.publicationYear ?? null,
    open_count: 0,
    user_id: user.id,
  });

  let imported = 0;
  let failed = 0;
  for (let i = 0; i < toInsert.length; i += IMPORT_BATCH) {
    const slice = toInsert.slice(i, i + IMPORT_BATCH);
    const { error } = await supabase.from('transmissions').insert(slice.map(buildRow) as any);
    if (error) {
      // One bad row shouldn't sink the whole batch — retry row by row so the
      // rest still land, and count only the genuinely failing ones.
      for (const b of slice) {
        const { error: rowErr } = await supabase.from('transmissions').insert([buildRow(b)] as any);
        if (rowErr) {
          failed++;
          console.error('Import row failed:', b.title, rowErr.message);
        } else {
          imported++;
        }
      }
    } else {
      imported += slice.length;
    }
    onProgress?.(Math.min(i + IMPORT_BATCH, toInsert.length), toInsert.length);
  }

  return { imported, skippedDuplicates, skippedInvalid, failed };
};